import { CheckCircle2, Circle, ArrowRight } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"

/**
 * Stable identity for the two inputs the copy has to name out loud. `label` is a
 * display string and may be reworded; this may not.
 */
type AnalysisItemKind = 'evaluation_criteria' | 'compliance_matrix'

interface AnalysisItem {
  label: string
  done: boolean
  required: boolean
  /** Ran to completion and returned nothing — settled, not outstanding. */
  settledEmpty?: boolean
  kind?: AnalysisItemKind
}

/**
 * The line to show when every required input is settled but scoring still isn't
 * possible. It must name whichever input actually came back empty — saying "this
 * pack doesn't define any evaluation criteria" under a green-ticked Evaluation
 * criteria row is a plain falsehood. `null` when nothing is settled-empty.
 */
function settledEmptyCopy(criteriaEmpty: boolean, complianceEmpty: boolean): string | null {
  if (criteriaEmpty && complianceEmpty) {
    return 'AI scoring needs evaluation criteria and a compliance matrix — this pack defines neither.'
  }
  if (criteriaEmpty) return "AI scoring needs evaluation criteria — this pack doesn't define any."
  if (complianceEmpty) return "AI scoring needs a compliance matrix — this pack doesn't define any."
  return null
}

interface AiReadinessBannerProps {
  bidId: string
  items: AnalysisItem[]
  allRequiredDone: boolean
  scorableCount?: number
  totalCount?: number
}

export function AiReadinessBanner({ bidId, items, allRequiredDone, scorableCount, totalCount }: AiReadinessBannerProps) {
  const navigate = useNavigate()

  // CTA: every row — including the optional ones — has either produced data or
  // completed empty, so there is nothing left for the user to run.
  const allSettled = items.every((i) => i.done || i.settledEmpty === true)

  // Copy: scoped to the REQUIRED rows only. The sentence is about why AI scoring
  // is impossible, which an outstanding tender summary has no bearing on.
  const requiredSettled = items
    .filter((i) => i.required)
    .every((i) => i.done || i.settledEmpty === true)
  const isEmptyInput = (kind: AnalysisItemKind) => {
    const item = items.find((i) => i.kind === kind)
    return item != null && !item.done && item.settledEmpty === true
  }
  const settledLine = requiredSettled
    ? settledEmptyCopy(isEmptyInput('evaluation_criteria'), isEmptyInput('compliance_matrix'))
    : null

  if (allRequiredDone && items.every(i => i.done)) return null

  return (
    <div className="rounded-lg bg-muted/40 px-4 py-3 mb-6">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-muted-foreground">AI Readiness</span>
        {!allRequiredDone && !allSettled && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => void navigate(`/app/bids/${bidId}/analysis`)}
          >
            Run Analysis
            <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {items.map(item => (
          <div key={item.label} className="flex items-center gap-1.5">
            {item.done ? (
              <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--status-success))]" />
            ) : (
              <Circle className="h-3.5 w-3.5 text-muted-foreground" />
            )}
            <span className="text-meta">
              {item.label}
              {item.required && !item.done && !item.settledEmpty && (
                <span className="text-[hsl(var(--destructive))]"> *</span>
              )}
            </span>
          </div>
        ))}
      </div>

      {allRequiredDone && scorableCount != null && totalCount != null && (
        <p className="text-meta mt-2">
          AI can score {scorableCount} of {totalCount} criteria.
          {items.some(i => !i.done) && ' Run remaining analysis to improve results.'}
        </p>
      )}

      {!allRequiredDone && (
        <p className="text-meta mt-2">
          {settledLine ?? 'Run evaluation criteria and compliance matrix analysis to enable AI scoring.'}
        </p>
      )}
    </div>
  )
}
